import React, { useState, useMemo, useEffect } from "react";
import { Button, Box, CircularProgress, Chip } from "@mui/material";
import Table from "../../components/Table/Table";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import AssignPackModal from "./AssignPackModal";
import EditAssignmentModal from "./EditAssignmentModal";
import {
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
} from "../../store/services/tenancy.service";

ActiveContentPackTable.propTypes = {
    data: PropTypes.array,
    isLoading: PropTypes.bool,
    error: PropTypes.any,
    paginationModel: PropTypes.object,
    setPaginationModel: PropTypes.func,
    contentPacks: PropTypes.array,
    groups: PropTypes.array,
    onAssignConfirm: PropTypes.func,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    isCreating: PropTypes.bool,
    isUpdating: PropTypes.bool
};

export default function ActiveContentPackTable({
    data = [],
    isLoading = false,
    error = null,
    paginationModel,
    setPaginationModel,
    contentPacks = [],
    groups = [],
    onAssignConfirm,
    onEdit,
    onDelete,
    isCreating = false,
    isUpdating = false
}) {
    const theme = useTheme();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [deletingId, setDeletingId] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);

    // Lazy queries for resolving scopeId to names
    const [triggerLocations, { data: locationsData }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerCities, { data: citiesData }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerCountries, { data: countriesData }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData }] = useLazyGetCompanyNamesByTenancyIdQuery();
    const [triggerStates, { data: statesData }] = useLazyGetStatesByTenancyIdQuery();

    // Fetch grouping data for scopes used in assignments
    const usedScopes = useMemo(() =>
        [...new Set(data.map(a => a.scope || a.groupingType).filter(Boolean))],
    [data]);

    useEffect(() => {
        if (!tenancyId) return;
        if (usedScopes.includes('Location')) triggerLocations({ tenancyId });
        if (usedScopes.includes('Department')) triggerDepartments({ tenancyId });
        if (usedScopes.includes('City')) triggerCities({ tenancyId });
        if (usedScopes.includes('Country')) triggerCountries({ tenancyId });
        if (usedScopes.includes('CompanyName')) triggerCompanyNames({ tenancyId });
        if (usedScopes.includes('State')) triggerStates({ tenancyId });
    }, [usedScopes, tenancyId]);

    // Build a scope-keyed lookup map: { "Department": { "15": "Software" }, "Group": { "2": "without-servers" }, ... }
    const scopeNameLookup = useMemo(() => {
        const map = {};
        const addItems = (scope, items) => {
            map[scope] = {};
            (items || []).forEach(i => { map[scope][String(i.id)] = i.name; });
        };
        addItems('Location', locationsData);
        addItems('Department', departmentsData);
        addItems('City', citiesData);
        addItems('Country', countriesData);
        addItems('CompanyName', companyNamesData);
        addItems('State', statesData);
        map['Group'] = {};
        (groups || []).forEach(g => { map['Group'][String(g.userGroupId)] = g.name; });
        return map;
    }, [locationsData, departmentsData, citiesData, countriesData, companyNamesData, statesData, groups]);

    const handleDelete = async (row) => {
        if (onDelete) {
            setDeletingId(row.id);
            await onDelete(row.id);
            setDeletingId(null);
        }
    };

    const handleAssignPack = () => {
        setAssignModalOpen(true);
    };

    const handleAssignConfirm = (assignData) => {
        if (onAssignConfirm) {
            onAssignConfirm(assignData);
        }
    };

    const handleEdit = (row) => {
        const assignment = data.find(a => a.id === row.id);
        if (assignment) {
            setEditingAssignment(assignment);
            setEditModalOpen(true);
        }
    };

    const handleEditConfirm = async (editData) => {
        if (onEdit) {
            await onEdit(editData);
            setEditModalOpen(false);
            setEditingAssignment(null);
        }
    };

    // Transform assignment data for table display
    // Backend uses: scope, scopeId, refreshEnabled, contentPackId
    // Frontend displays: contentPackName, recipient, annualRefresh
    const tableData = useMemo(() => {
        return data.map(assignment => {
            const scope = assignment.scope || assignment.groupingType;
            const scopeId = assignment.scopeId;
            let recipient = scope || '-';
            if (scope && scope !== 'Tenancy' && scopeId) {
                // Resolve integer ID to display name using scope-keyed lookup
                const resolvedName = scopeNameLookup[scope]?.[String(scopeId)];
                recipient = `${scope}: ${resolvedName || scopeId}`;
            }

            // Resolve content pack name from contentPacks list
            const pack = contentPacks.find(p => p.internalId === assignment.contentPackId);
            const contentPackName = pack?.contentPackName || assignment.contentPackName || '-';

            return {
                ...assignment,
                contentPackName,
                recipient,
                annualRefresh: assignment.refreshEnabled ?? assignment.annualRefresh ?? false
            };
        });
    }, [data, contentPacks, scopeNameLookup]);

    const toolbarActions = [
        {
            render: () => (
                <Box width="100%" justifyContent="flex-end" display="flex" gap={2}>
                    <Button
                        size="medium"
                        variant="contained"
                        sx={{ textTransform: "capitalize", whiteSpace: "nowrap", ml: 2 }}
                        onClick={handleAssignPack}
                        endIcon={<AddIcon />}
                    >
                        Assign Pack
                    </Button>
                </Box>
            )
        }
    ];

    const headCells = [
        {
            id: "contentPackName",
            label: "Content Pack Name",
            initialVisible: true
        },
        {
            id: "recipient",
            label: "Recipient",
            initialVisible: true
        },
        {
            id: "annualRefresh",
            label: "Annual Refresh",
            render: (dataRow) => (
                dataRow.annualRefresh ?
                    <Chip label="Yes" size="small" sx={{ bgcolor: '#33BD95', color: '#fff', fontWeight: 600 }} /> :
                    <Chip label="No" size="small" sx={{ bgcolor: '#e0e0e0', color: '#666', fontWeight: 600 }} />
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "Edit",
            render: (dataRow) => (
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => handleEdit(dataRow)}
                    sx={{
                        minWidth: '70px',
                        width: '70px',
                        height: '32px',
                        padding: '6px 8px',
                    }}
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                >
                    Edit
                </Button>
            ),
            initialVisible: true
        },
        {
            id: "delete",
            label: "Delete",
            render: (dataRow) => (
                <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => handleDelete(dataRow)}
                    disabled={deletingId === dataRow.id}
                    sx={{
                        minWidth: '70px',
                        width: '70px',
                        height: '32px',
                        padding: '6px 8px',
                    }}
                >
                    {deletingId === dataRow.id ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        "Delete"
                    )}
                </Button>
            ),
            initialVisible: true
        }
    ];

    return (
        <>
            <Table
                name="Active Content Packs"
                data={tableData}
                error={error}
                loading={isLoading}
                headCells={headCells}
                search={true}
                editColumn={false}
                checkbox={false}
                selected={[]}
                showDelete={false}
                toolbarActions={toolbarActions}
                showToolbarActions={true}
                maxTableHeight="calc(100vh - 300px)"
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />
            <AssignPackModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                contentPacks={contentPacks}
                groups={groups}
                onConfirm={handleAssignConfirm}
                isLoading={isCreating}
            />
            <EditAssignmentModal
                open={editModalOpen}
                onClose={() => { setEditModalOpen(false); setEditingAssignment(null); }}
                assignment={editingAssignment}
                contentPacks={contentPacks}
                groups={groups}
                onConfirm={handleEditConfirm}
                isLoading={isUpdating}
            />
        </>
    );
}
